<template>
	<div class="w-full p-6 print:py-3">
		<div
			class="flex items-start justify-between gap-4 print:border-b-2 print:border-slate-900"
			:class="activePlan.neverServed ? 'mb-0' : 'mb-4'"
		>
			<h2 class="form-title text-2xl font-bold print:pt-2 print:text-sm/0">
				{{ t('plan.sectionTitle.militaryRecord') }}
			</h2>

			<FormKit
				type="checkbox"
				name="neverServed"
				wrapper-class="!mb-0"
				outer-class="mt-0 !mb-0 grow-0 self-center flex items-center print:pb-1"
				:label="t('plan.neverServed')"
			/>
		</div>
		<div v-if="!activePlan.neverServed">
			<div
				class="section-grid-row grid grid-cols-1 overflow-x-auto px-1 pb-7 *:w-full *:max-w-none print:pb-4"
			>
				<PictureCardCheckboxes
					v-model="activePlan.militaryRecord"
					class="print:hidden"
					name="militaryRecord"
					:options="militaryRecordOptions"
				/>

				<FormKit
					type="checkbox"
					name="militaryRecord"
					outer-class="mt-0 hidden print:block"
					legend-class="text-[11px] text-secondary-400 font-normal uppercase"
					option-class="mb-1.5 md:mb-0"
					options-class="flex print:flex-row print:gap-6 md:items-center pt-2 md:gap-6"
					:label="t('plan.branchOfService')"
					:options="militaryRecordOptions"
				/>
			</div>
			<div class="section-grid-row grid *:w-full *:max-w-none sm:grid-cols-1">
				<FormKit
					type="checkbox"
					name="militaryRequests"
					outer-class="mt-0"
					legend-class="text-[11px] text-secondary-400 font-normal uppercase"
					option-class="w-full sm:w-fit"
					options-class="flex flex-wrap pt-2 sm:gap-x-6"
					:label="t('plan.militaryRequests')"
					:options="militaryRequestOptions"
				/>
			</div>
			<div
				class="section-grid-row grid grid-cols-1 *:w-full *:max-w-none *:px-1"
				:class="{ 'print:hidden': !activePlan?.militaryRecordNotes?.trim() }"
			>
				<FormKit
					type="textarea"
					auto-height
					name="militaryRecordNotes"
					:label="t('plan.notes')"
					:outer-class="{ 'mb-0': true }"
					:placeholder="t('plan.placeholders.militaryRecordNotes')"
					validation="length:0,10000"
					validation-visibility="live"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import airforce from '@/assets/US-military-seals-airforce-80x80.png'
import army from '@/assets/US-military-seals-army-80x80.png'
import coastguard from '@/assets/US-military-seals-coastguard-80x80.png'
import marine from '@/assets/US-military-seals-marine-80x80.png'
import nationalguard from '@/assets/US-military-seals-nationalguard-80x80.png'
import navy from '@/assets/US-military-seals-navy-80x80.png'
import spaceforce from '@/assets/US-military-seals-spaceforce-80x80.png'
import PictureCardCheckboxes from '@/components/memorialGuide/PictureCardCheckboxes.vue'
import { MilitaryRecord } from '@/enums/MilitaryRecord'
import { MilitaryRequests } from '@/enums/MilitaryRequests'
import { usePresentationLocale } from '@/i18n'
import { FormKit } from '@formkit/vue'

const { t } = usePresentationLocale()

defineProps<{
	activePlan: any // TODO: Add proper type
}>()

/**
 * The options for military record radio cards.
 */
const militaryRecordOptions = [
	{
		label: t('plan.militaryRecordOptions.army'),
		value: MilitaryRecord.Army,
		imageUrl: army,
		fullWidth: true,
	},
	{
		label: t('plan.militaryRecordOptions.marineCorp'),
		value: MilitaryRecord.MarineCorp,
		imageUrl: marine,
		fullWidth: true,
	},
	{
		label: t('plan.militaryRecordOptions.navy'),
		value: MilitaryRecord.Navy,
		imageUrl: navy,
		fullWidth: true,
	},
	{
		label: t('plan.militaryRecordOptions.airForce'),
		value: MilitaryRecord.AirForce,
		imageUrl: airforce,
		fullWidth: true,
	},
	{
		label: t('plan.militaryRecordOptions.spaceForce'),
		value: MilitaryRecord.SpaceForce,
		imageUrl: spaceforce,
		fullWidth: true,
	},
	{
		label: t('plan.militaryRecordOptions.coastGuard'),
		value: MilitaryRecord.CoastGuard,
		imageUrl: coastguard,
		fullWidth: true,
	},
	{
		label: t('plan.militaryRecordOptions.nationalGuard'),
		value: MilitaryRecord.NationalGuard,
		imageUrl: nationalguard,
		fullWidth: true,
	},
]

const militaryRequestOptions = [
	{
		label: t('plan.militaryRequestOptions.ddRequested'),
		value: MilitaryRequests.DDRequested,
	},
	{
		label: t('plan.militaryRequestOptions.honors'),
		value: MilitaryRequests.Honors,
	},
	{
		label: t('plan.militaryRequestOptions.militaryMarker'),
		value: MilitaryRequests.MilitaryMarker,
	},
	{
		label: t('plan.militaryRequestOptions.flagDraped'),
		value: MilitaryRequests.FlagDraped,
	},
	{
		label: t('plan.militaryRequestOptions.flagFolded'),
		value: MilitaryRequests.FlagFolded,
	},
	{
		label: t('plan.militaryRequestOptions.presidentialMemorialCertificate'),
		value: MilitaryRequests.PresidentialMemorialCertificate,
	},
	{
		label: t('plan.militaryRequestOptions.veteransCemetery'),
		value: MilitaryRequests.VeteransCemetery,
	},
]
</script>
